/*
 * Copyright (c) 2022 by N-Ach (Pvt) Ltd
 *
 * ╔═╗ ╔╗    ╔═══╗  ╔╗
 * ║║╚╗║║    ║╔═╗║  ║║
 * ║╔╗╚╝║    ║║ ║╠══╣╚═╗
 * ║║╚╗║║╔══╗║╚═╝║╔═╣╔╗║
 * ║║ ║║║╚══╝║╔═╗║╚═╣║║║
 * ╚╝ ╚═╝    ╚╝ ╚╩══╩╝╚╝
 *
 * All rights reserved.
 *
 * This software is the confidential and proprietary information
 * of N-Ach (Pvt) Ltd. You shall not disclose such Confidential
 * Information and shall use it only in accordance with the terms
 * of the license agreement you entered into with N-Ach (Pvt) Ltd.
 *
 *
 * @Author Dilhari Jayathunga
 * @Date 14/7/2022
 */


import React, { useState, useEffect } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import * as yup from "yup";
import { Button, OutlinedInput, Typography, IconButton, Grid } from "@mui/material";
import { Close } from "@mui/icons-material";
import {
  useGetOfferByIdQuery,
  useUpdateOfferMutation,
} from "../../Store/Services/OfferService";
import {useGetAllCatWithSubCatQuery} from "../../Store/Services/CategoryService";
import CategoryList from "../Shared/CategoryList";
import styles from "./Offer.module.css";

const schema = yup
  .object({
    off: yup.string().required(""),
    offerDesc: yup.string().required(""),
    day: yup.string().required(""),
    catId: yup.string().required(),
  })
  .required();

const EditOffer = ({ handleClose }) => {
  const [openCatList, setOpenCatList] = useState(false);
  const [catName, setCatName] = useState("");
  const [selectedCat, setSelectedCat] = useState("");
  const { data: categories, isSuccess: categoriesSuccess } = useGetAllCatWithSubCatQuery();


  const offer = useSelector((state) => state.offer);

  const {
    data: offerDetails,
    isSuccess: offerSuccess,
    isLoading: offerLoading,
  } = useGetOfferByIdQuery(offer.selectedOffer);


  
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });
  
  useEffect(() => {
    if (offerSuccess && Array.isArray(offerDetails) && offerDetails.length > 0 && categoriesSuccess) {
      const firstOffer = offerDetails[0];
      const category = categories.find((cat) => cat.catId === firstOffer.catId);

      reset({
        offerDesc: firstOffer?.offerDesc,
        off: firstOffer?.off,
        day: firstOffer?.day,
        catId: firstOffer?.catId,
      });

      setCatName(category?.catName);
      setSelectedCat(firstOffer?.catId);
    }
  }, [offerDetails, offerSuccess, categories, categoriesSuccess, reset, setCatName, setSelectedCat]);
  
  
  
  const [updateOffer] = useUpdateOfferMutation();

  const onSubmit = async (data) => {

    const dataSet = {
      ...data,
      offerId: offer.selectedOffer,
      catId: selectedCat,
    };


    try {
      const respond = await updateOffer(dataSet).unwrap();
      toast.success("Offer successfully updated!",  {position:'bottom-right'});
    } catch (err) {
      console.error("Failed to save Offer: ", err);
      toast.error("Error, something went wrong!",  {position:'bottom-right'});
    }

    handleClose();
  };

  return (
    <>
      <div className={styles.headingContainer}>
        <Typography fontWeight={600} variant={"h6"}>
          Edit Offer
        </Typography>
        <IconButton className={styles.closeButton} onClick={handleClose}>
          <Close />
        </IconButton>
      </div>

      <form onSubmit={handleSubmit(onSubmit)}>
      <Grid item xs={12} sx={{ width: "100%" }}>     
        <div className={styles.formItem}>
          <label className={styles.formLabel}>Offer Description:</label>
          {errors.offerDesc && errors.offerDesc.type === "required" && (
            <span className={styles.validation}> *</span>
          )}
          <OutlinedInput
            {...register("offerDesc")}
            className={styles.formInput}
            placeholder="Enter Description"
            size="small"
            fullWidth
          />
        </div>
        </Grid>
        <Grid item xs={12} sx={{ width: "100%" }}>
        <div className={styles.formItem}>
          <label className={styles.formLabel}>Category:</label>
          {selectedCat === "" && <span className={styles.validation}> *</span>}
          <OutlinedInput
            className={styles.formInput}
            placeholder="Select Category"
            size="small"
            onClick={() => setOpenCatList(true)}
            fullWidth
            autoComplete="off"
            value={catName}
          />
        </div>
        </Grid>
        <Grid item container xs={12} columnSpacing={2} rowSpacing={1}>
          <Grid item xs={12} md={6} sx={{ width: "50%" }}>
        <div className={styles.formItem}>
          <label className={styles.formLabel}>Day:</label>
          {errors.day && errors.day.type === "required" && (
            <span className={styles.validation}> *</span>
          )}
          <OutlinedInput
            {...register("day")}
            className={styles.formInput}
            type="date"
            placeholder="Enter Day"
            size="small"
            fullWidth
          />
        </div>
        </Grid>
        <Grid item xs={12} md={6} sx={{ width: "50%" }}>
        <div className={styles.formItem}>
          <label className={styles.formLabel}>Offer %:</label>
          {errors.off && errors.off.type === "required" && (
            <span className={styles.validation}> *</span>
          )}
          <OutlinedInput
            {...register("off")}
            className={styles.formInput}
            placeholder="Enter Offer"
            type="number"
            size="small"
            fullWidth
          />
        </div>
        </Grid>
        </Grid>
        <div className={styles.buttonContainer}>
          <Button
            type="submit"
            className={styles.submitButton}
            variant="contained"
          >
            Save
          </Button>
        </div>
      </form>

      <CategoryList
        open={openCatList}
        onClose={() => setOpenCatList(false)}
        setSelectedCat={setSelectedCat}
        selectedCat={selectedCat}
        setCatName={setCatName}
      />
    </>
  );
};

export default EditOffer;
